import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Calendar, CheckCircle2, Clock, Smile, Target, Zap, X } from 'lucide-react';
import { DoodleSquiggle, DoodleStar, DoodleFlower } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_EMOJIS = { 1: '😢', 2: '😟', 3: '😐', 4: '😊', 5: '🤩' };
const MOOD_COLORS = { 1: '#FF6B6B', 2: '#FFC8DD', 3: '#FDF0D5', 4: '#B9FBC0', 5: '#A2D2FF' };
const LANE_COLORS = { today: '#86efac', week: '#fde68a', later: '#fecdd3' };
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CalendarOverview() {
  const [view, setView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const monthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/calendar/overview?month=${monthStr}`, { withCredentials: true });
      setData(d);
    } catch (e) { console.error('Failed to fetch calendar:', e); }
    finally { setLoading(false); }
  }, [monthStr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const navigate = (delta) => {
    const d = new Date(currentDate);
    if (view === 'month') d.setMonth(d.getMonth() + delta);
    else d.setDate(d.getDate() + delta * 7);
    setCurrentDate(d);
    setSelectedDay(null);
  };

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const getEventsForDay = (dateStr) => {
    if (!data) return { tasks: [], schedules: [], sessions: [], mood: null, habits: 0 };
    return {
      tasks: data.tasks.filter(t => t.date === dateStr),
      schedules: data.schedules.filter(s => s.date === dateStr),
      sessions: data.sessions.filter(s => s.date === dateStr),
      mood: data.moods[dateStr] || null,
      habits: data.habits[dateStr] || 0,
    };
  };

  // Build month grid
  const buildMonthGrid = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ day: d, date: dateStr, ...getEventsForDay(dateStr) });
    }
    return cells;
  };

  // Build week view
  const buildWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(d.getDate() + i);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      days.push({ day: d.getDate(), dayName: DAYS[i], date: dateStr, fullDate: d, ...getEventsForDay(dateStr) });
    }
    return days;
  };

  const monthLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const weekLabel = (() => {
    const days = buildWeekDays();
    return `${days[0].date.slice(5)} — ${days[6].date.slice(5)}`;
  })();

  return (
    <div className="space-y-4" data-testid="calendar-overview">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-6 h-6" />
          <h2 className="text-2xl font-bold font-['Fredoka']">Calendar</h2>
        </div>
        <div className="flex gap-2">
          {['month', 'week'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`neo-btn px-3 py-1.5 text-sm capitalize ${view === v ? 'bg-[#A2D2FF]' : 'bg-white dark:bg-[#252545]'}`}
              data-testid={`view-${v}`}>{v}</button>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="neo-btn p-2 bg-white dark:bg-[#252545]" data-testid="cal-prev">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <h3 className="text-xl font-bold font-['Fredoka']">{view === 'month' ? monthLabel : weekLabel}</h3>
          <DoodleSquiggle width={60} color="#CDB4DB" className="mx-auto mt-1" />
        </div>
        <button onClick={() => navigate(1)} className="neo-btn p-2 bg-white dark:bg-[#252545]" data-testid="cal-next">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {loading ? (
        <div className="neo-card p-8 text-center animate-pulse-soft">Loading calendar...</div>
      ) : view === 'month' ? (
        /* ============ MONTH VIEW ============ */
        <div className="neo-card p-4" style={{
          backgroundImage: 'radial-gradient(circle, #e8e8e8 0.5px, transparent 0.5px)',
          backgroundSize: '16px 16px'
        }}>
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs font-bold text-gray-400 uppercase py-1">{d}</div>
            ))}
          </div>
          {/* Cells */}
          <div className="grid grid-cols-7 gap-1">
            {buildMonthGrid().map((cell, i) => {
              if (!cell) return <div key={`e${i}`} />;
              const isToday = cell.date === todayStr;
              const hasEvents = cell.tasks.length > 0 || cell.schedules.length > 0;
              const tasksDone = cell.tasks.filter(t => t.completed).length;
              const totalTasks = cell.tasks.length;
              
              return (
                <button key={cell.date} onClick={() => setSelectedDay(cell)}
                  className={`aspect-square rounded-xl p-1 text-left transition-all hover:scale-105 relative ${
                    isToday ? 'ring-2 ring-black dark:ring-white' : ''
                  } ${selectedDay?.date === cell.date ? 'bg-[#A2D2FF]/30' : 'bg-white/60 dark:bg-[#252545]/60'}`}
                  data-testid={`cal-day-${cell.date}`}
                >
                  <span className={`text-xs font-bold ${isToday ? 'text-blue-600' : 'text-gray-600 dark:text-gray-300'}`}>{cell.day}</span>
                  
                  {/* Indicators */}
                  <div className="flex flex-wrap gap-0.5 mt-0.5">
                    {totalTasks > 0 && (
                      <div className={`w-2 h-2 rounded-full ${tasksDone === totalTasks ? 'bg-green-400' : 'bg-amber-400'}`} title={`${tasksDone}/${totalTasks} tasks`} />
                    )}
                    {cell.schedules.length > 0 && <div className="w-2 h-2 rounded-full bg-blue-400" title="Schedule" />}
                    {cell.sessions.length > 0 && <div className="w-2 h-2 rounded-full bg-purple-400" title="Focus" />}
                    {cell.habits > 0 && <div className="w-2 h-2 rounded-full bg-pink-400" title="Habits" />}
                  </div>
                  
                  {/* Mood emoji */}
                  {cell.mood && (
                    <span className="absolute bottom-0.5 right-0.5 text-[10px]">{MOOD_EMOJIS[cell.mood]}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex gap-4 mt-3 pt-2 border-t border-dashed border-gray-300 justify-center flex-wrap">
            <span className="flex items-center gap-1 text-[10px]"><div className="w-2 h-2 rounded-full bg-green-400" /> Tasks done</span>
            <span className="flex items-center gap-1 text-[10px]"><div className="w-2 h-2 rounded-full bg-amber-400" /> Tasks pending</span>
            <span className="flex items-center gap-1 text-[10px]"><div className="w-2 h-2 rounded-full bg-blue-400" /> Schedule</span>
            <span className="flex items-center gap-1 text-[10px]"><div className="w-2 h-2 rounded-full bg-purple-400" /> Focus</span>
            <span className="flex items-center gap-1 text-[10px]"><div className="w-2 h-2 rounded-full bg-pink-400" /> Habits</span>
          </div>
        </div>
      ) : (
        /* ============ WEEK VIEW ============ */
        <div className="grid grid-cols-7 gap-2">
          {buildWeekDays().map(day => {
            const isToday = day.date === todayStr;
            return (
              <button key={day.date} onClick={() => setSelectedDay(day)}
                className={`neo-card p-3 text-center transition-all hover:scale-[1.02] ${
                  isToday ? 'ring-2 ring-black dark:ring-white' : ''
                } ${selectedDay?.date === day.date ? 'bg-[#A2D2FF]/30' : ''}`}
                data-testid={`week-day-${day.date}`}
              >
                <p className="text-xs font-bold text-gray-400 uppercase">{day.dayName}</p>
                <p className={`text-2xl font-bold font-['Fredoka'] mb-2 ${isToday ? 'text-blue-600' : ''}`}>{day.day}</p>
                
                {day.mood && <span className="text-lg block mb-1">{MOOD_EMOJIS[day.mood]}</span>}
                
                {day.tasks.length > 0 && (
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <CheckCircle2 className="w-3 h-3 text-green-500" />
                    <span className="text-xs font-bold">{day.tasks.filter(t => t.completed).length}/{day.tasks.length}</span>
                  </div>
                )}
                {day.sessions.length > 0 && (
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Clock className="w-3 h-3 text-purple-500" />
                    <span className="text-xs">{day.sessions.reduce((s, e) => s + e.duration, 0)}m</span>
                  </div>
                )}
                {day.schedules.length > 0 && (
                  <p className="text-[10px] text-gray-400">{day.schedules.length} scheduled</p>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ============ DAY DETAIL PANEL ============ */}
      {selectedDay && (
        <div className="neo-card p-5 animate-bounce-in" data-testid="day-detail">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold font-['Fredoka']">
              {new Date(selectedDay.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h3>
            <button onClick={() => setSelectedDay(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><X className="w-5 h-5" /></button>
          </div>
          
          {selectedDay.mood && (
            <div className="flex items-center gap-2 mb-3 p-2 rounded-xl" style={{ backgroundColor: MOOD_COLORS[selectedDay.mood] + '40' }}>
              <span className="text-2xl">{MOOD_EMOJIS[selectedDay.mood]}</span>
              <span className="font-semibold text-sm">Mood: {['', 'Terrible', 'Bad', 'Okay', 'Good', 'Amazing'][selectedDay.mood]}</span>
            </div>
          )}
          
          {/* Tasks */}
          {selectedDay.tasks.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-bold uppercase text-gray-400 mb-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Tasks ({selectedDay.tasks.length})
              </p>
              {selectedDay.tasks.map((t, i) => (
                <div key={i} className="flex items-center gap-2 py-1 text-sm">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LANE_COLORS[t.lane] || '#ccc' }} />
                  <span className={t.completed ? 'line-through text-gray-400' : ''}>{t.title}</span>
                  <span className="ml-auto text-xs text-gray-400">+{t.points}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Schedule */}
          {selectedDay.schedules.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-bold uppercase text-gray-400 mb-1 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Schedule ({selectedDay.schedules.length})
              </p>
              {selectedDay.schedules.map((s, i) => (
                <div key={i} className="flex items-center gap-2 py-1 text-sm">
                  <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{s.time}</span>
                  <span>{s.title}</span>
                </div>
              ))}
            </div>
          )}
          
          {/* Focus */}
          {selectedDay.sessions.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-bold uppercase text-gray-400 mb-1 flex items-center gap-1">
                <Zap className="w-3 h-3" /> Focus Sessions ({selectedDay.sessions.length})
              </p>
              <p className="text-sm">Total: {selectedDay.sessions.reduce((s, e) => s + e.duration, 0)} minutes</p>
            </div>
          )}
          
          {/* Habits */}
          {selectedDay.habits > 0 && (
            <div>
              <p className="text-xs font-bold uppercase text-gray-400 mb-1 flex items-center gap-1">
                <Target className="w-3 h-3" /> Habits: {selectedDay.habits} completed
              </p>
            </div>
          )}
          
          {selectedDay.tasks.length === 0 && selectedDay.schedules.length === 0 && selectedDay.sessions.length === 0 && !selectedDay.mood && (
            <p className="text-sm text-gray-400 text-center py-4">No activity this day</p>
          )}
        </div>
      )}
    </div>
  );
}
